import {
  FETCH_TASKS,
  FETCH_TASKS_SUCCESS,
  FETCH_TASKS_FAILED,
  ACCEPT_TASK,
  CANCEL_TASK,
} from "../store/types";
import store from "../store/store";
import { updateProfile, getValidAuthToken, getEffectiveUserRole } from "./authactions";
import { firebase } from '../config/configureFirebase';
import { onValue, off, push } from "firebase/database";
import { driverQueue } from '../other/sharedFunctions';

const resolveFunctionsBaseUrl = (config) => {
  const envUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : '';
  if (envUrl) return envUrl;
  const projectId = config?.projectId || firebase.config?.projectId || 'drive2go-c806b';
  const region = (typeof process !== 'undefined' && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || config?.functionsRegion || firebase.config?.functionsRegion || 'europe-west1';
  return `https://${region}-${projectId}.cloudfunctions.net`;
};

export const stopTasks = () => (dispatch, getState) => {
  const { auth, tasksRef } = firebase;
  const state = typeof getState === 'function' ? getState() : null;
  const uid = auth?.currentUser?.uid || state?.auth?.profile?.uid || store.getState()?.auth?.profile?.uid;
  if (uid && tasksRef) {
    try {
      off(tasksRef(uid));
    } catch (_) { }
  }
  dispatch({
    type: FETCH_TASKS_FAILED,
    payload: store.getState()?.languagedata?.defaultLanguage?.no_tasks || 'no_tasks',
  });
};

export const fetchTasks = () => (dispatch, getState) => {
  const { auth, tasksRef, config } = firebase;

  const state = typeof getState === 'function' ? getState() : null;
  const profile = state?.auth?.profile || store.getState()?.auth?.profile;
  const uid = auth?.currentUser?.uid || profile?.uid;
  if (!uid || !tasksRef) {
    return;
  }

  const targetRef = tasksRef(uid);
  try {
    off(targetRef);
  } catch (_) { }

  if (profile && getEffectiveUserRole(profile) !== 'driver') {
    dispatch({
      type: FETCH_TASKS_FAILED,
      payload: store.getState()?.languagedata?.defaultLanguage?.no_tasks || 'no_tasks',
    });
    return;
  }

  dispatch({
    type: FETCH_TASKS,
    payload: null,
  });

  const dispatchTasksData = (data) => {
    if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      const arr = Object.keys(data)
        .filter((i) => data[i] && typeof data[i] === 'object' && !data[i].tombstone && data[i].status !== 'CLOSED')
        .map((i) => {
          data[i].id = i;
          return data[i];
        });
      if (arr.length > 0) {
        dispatch({
          type: FETCH_TASKS_SUCCESS,
          payload: arr,
        });
        return;
      }
    }
    dispatch({
      type: FETCH_TASKS_FAILED,
      payload: store.getState()?.languagedata?.defaultLanguage?.no_tasks || 'no_tasks',
    });
  };

  onValue(
    targetRef,
    (snapshot) => {
      dispatchTasksData(snapshot.val());
    },
    async (_err) => {
      // Fallback via RTDB REST with valid auth token if JS WebSocket session is not ready on mobile
      try {
        const token = await getValidAuthToken();
        if (token) {
          const rtdbBase = (config?.databaseURL || firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
          const res = await fetch(`${rtdbBase}/driver_tasks/${uid}.json?auth=${token}`);
          if (res.ok) {
            const data = await res.json();
            dispatchTasksData(data);
            return;
          }
        }
      } catch (_) { }
      dispatchTasksData(null);
    }
  );
};

export const acceptTask = (task) => async (dispatch, getState) => {
  const { auth, trackingRef, config } = firebase;

  const state = typeof getState === 'function' ? getState() : null;
  const uid = auth?.currentUser?.uid || state?.auth?.profile?.uid || store.getState()?.auth?.profile?.uid;
  if (!uid || !task?.id) return;

  try {
    let token = await getValidAuthToken();
    const baseUrl = resolveFunctionsBaseUrl(config);
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response = await fetch(`${baseUrl}/acceptBookingTask`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        bookingId: task.id,
        driverDeviceId: task.driverDeviceId || null
      })
    });

    if (response.status === 401) {
      token = await getValidAuthToken(true);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        response = await fetch(`${baseUrl}/acceptBookingTask`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            bookingId: task.id,
            driverDeviceId: task.driverDeviceId || null
          })
        });
      }
    }

    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result?.success) {
      console.warn('[acceptTask] Server rejected acceptance:', result?.error || response.status);
      dispatch({
        type: CANCEL_TASK,
        payload: { uid, bookingId: task.id }
      });
      return result;
    }

    const acceptedBooking = result.booking ? { ...task, ...result.booking, id: task.id } : task;

    try {
      const driverLocation = store.getState()?.gpsdata?.location;
      if (trackingRef && driverLocation && Number.isFinite(driverLocation.lat) && Number.isFinite(driverLocation.lng)) {
        push(trackingRef(task.id), {
          at: new Date().getTime(),
          status: "ACCEPTED",
          lat: driverLocation.lat,
          lng: driverLocation.lng,
        });
      }
    } catch (_) { }

    dispatch({
      type: ACCEPT_TASK,
      payload: { task: acceptedBooking },
    });

    return result;
  } catch (error) {
    console.error('[acceptTask] Error:', error);
    return { error: error.message };
  }
};

export const cancelTask = (bookingId) => async (dispatch, getState) => {
  const { auth, config } = firebase;

  const state = typeof getState === 'function' ? getState() : null;
  const uid = auth?.currentUser?.uid || state?.auth?.profile?.uid || store.getState()?.auth?.profile?.uid;
  if (!uid || !bookingId) return;

  dispatch({
    type: CANCEL_TASK,
    payload: { uid: uid, bookingId: bookingId },
  });

  try {
    const token = await getValidAuthToken();
    const baseUrl = resolveFunctionsBaseUrl(config);
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    await fetch(`${baseUrl}/rejectBookingTask`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ bookingId })
    });
  } catch (error) {
    console.warn('[cancelTask] Server reject notice:', error?.message);
  }
};
